import {
  coerceEnumValue,
  parseTypeToken,
  type ParsedTypeToken,
  type TypeSchema,
} from "@peekie/case-spec";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { labelForEnum, labelForKey } from "@/lib/phone-json-labels";
import { cn } from "@/lib/utils";
import { SortFieldMark } from "./array-sort-ui";
import { MediaUrlField } from "./media-url-field";

const LONG_TEXT_KEYS = new Set([
  "body",
  "description",
  "question",
  "level_1",
  "level_2",
  "caption",
  "content_description",
  "statement",
  "truth_review",
  "bio",
  "correct_answer",
  "error_reply",
  "subject",
  "paragraphs",
]);

function tokenFromSchema(schema: TypeSchema | null): ParsedTypeToken {
  if (schema && typeof schema === "string") return parseTypeToken(schema);
  return { kind: "string" };
}

function inferToken(value: unknown, schema: TypeSchema | null): ParsedTypeToken {
  if (schema && typeof schema === "string") return parseTypeToken(schema);
  if (typeof value === "boolean") return { kind: "boolean" };
  if (typeof value === "number") return { kind: "number" };
  return { kind: "string" };
}

function useTextarea(key: string, value: unknown): boolean {
  if (LONG_TEXT_KEYS.has(key)) return true;
  return typeof value === "string" && (value.includes("\n") || value.length > 80);
}

export function PrimitiveField({
  fieldKey,
  path,
  value,
  schema,
  disabled,
  onChange,
}: {
  fieldKey: string;
  path: string;
  value: unknown;
  schema: TypeSchema | null;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const token = inferToken(value, schema);
  const label = labelForKey(fieldKey);
  const id = `pjf-${path.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  if (token.kind === "const") {
    return (
      <Field>
        <FieldLabel htmlFor={id}>
          {label}
          <SortFieldMark fieldKey={fieldKey} />
        </FieldLabel>
        <Input id={id} value={token.value} disabled readOnly />
      </Field>
    );
  }

  if (token.kind === "boolean") {
    const invert = fieldKey === "is_free";
    const checked = invert ? !Boolean(value) : Boolean(value);
    return (
      <Field orientation="horizontal" className="items-center justify-between">
        <FieldLabel htmlFor={id}>
          {label}
          <SortFieldMark fieldKey={fieldKey} />
        </FieldLabel>
        <Switch
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(v) => onChange(invert ? !v : v)}
        />
      </Field>
    );
  }

  if (token.kind === "number") {
    return (
      <Field>
        <FieldLabel htmlFor={id}>
          {label}
          <SortFieldMark fieldKey={fieldKey} />
        </FieldLabel>
        <Input
          id={id}
          type="number"
          value={typeof value === "number" && Number.isFinite(value) ? value : ""}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || raw === "-") {
              onChange(0);
              return;
            }
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : 0);
          }}
        />
      </Field>
    );
  }

  if (token.kind === "enum") {
    const current = typeof value === "string" ? value : "";
    const choices = token.values.filter((opt) => opt.length > 0);
    const selected = choices.includes(current)
      ? current
      : coerceEnumValue(current, choices, fieldKey);
    return (
      <Field>
        <FieldLabel htmlFor={id}>
          {label}
          <SortFieldMark fieldKey={fieldKey} />
        </FieldLabel>
        <select
          id={id}
          disabled={disabled}
          value={selected}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "h-7 w-full rounded-md border border-input bg-input/20 px-2 text-sm outline-none",
            "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {choices.map((opt) => (
            <option key={opt} value={opt}>
              {labelForEnum(fieldKey, opt)}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  const stringValue = value == null ? "" : String(value);
  const placeholder =
    token.kind === "datetime"
      ? "YYYY-MM-DDTHH:mm:ss"
      : token.kind === "date"
        ? "YYYY-MM-DD"
        : token.kind === "media_url" || token.kind === "https_url"
          ? "https://…"
          : undefined;

  if (token.kind === "media_url") {
    const mediaKind =
      fieldKey === "audio_url" || fieldKey.endsWith("_audio_url")
        ? "audio"
        : fieldKey === "video_url" || fieldKey.endsWith("_video_url")
          ? "video"
          : "image";
    return (
      <MediaUrlField
        id={id}
        label={label}
        fieldKey={fieldKey}
        value={stringValue}
        disabled={disabled}
        placeholder={placeholder}
        mediaKind={mediaKind}
        onChange={(v) => onChange(v)}
      />
    );
  }

  if (useTextarea(fieldKey, stringValue)) {
    return (
      <Field>
        <FieldLabel htmlFor={id}>
          {label}
          <SortFieldMark fieldKey={fieldKey} />
        </FieldLabel>
        <Textarea
          id={id}
          value={stringValue}
          disabled={disabled}
          placeholder={placeholder}
          rows={3}
          className="min-h-16 resize-y text-sm"
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }

  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        <SortFieldMark fieldKey={fieldKey} />
      </FieldLabel>
      <Input
        id={id}
        value={stringValue}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function OpaqueJsonField({
  fieldKey,
  path,
  value,
  disabled,
  onChange,
}: {
  fieldKey: string;
  path: string;
  value: unknown;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const id = `pjf-opaque-${path.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
  const text =
    value === undefined
      ? ""
      : typeof value === "string"
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <Field>
      <FieldLabel htmlFor={id}>{labelForKey(fieldKey)}（原始对象）</FieldLabel>
      <Textarea
        id={id}
        value={text}
        disabled={disabled}
        rows={6}
        className="min-h-24 resize-y font-mono text-xs"
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (!raw) {
            onChange({});
            return;
          }
          try {
            onChange(JSON.parse(raw) as unknown);
          } catch {
            // keep typing; parent only gets valid JSON on successful parse
          }
        }}
      />
    </Field>
  );
}

export function schemaTokenKind(
  schema: TypeSchema | null,
): ParsedTypeToken["kind"] | "structure" {
  if (!schema) return "structure";
  if (typeof schema === "string") return tokenFromSchema(schema).kind;
  return "structure";
}
